import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDeviceStore } from '../../../stores/deviceStore';
import { usePPGAnalysis, useConnectionState, usePPGSQIData, usePPGMovingAverage, useProcessedDataStore } from '../../../stores/processedDataStore';
import { useSensorContactStatus } from '../../../stores/systemStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { getIndexGuide } from '../../../constants/indexGuides';
import { AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { AnalysisMetricsService } from '../../../services/AnalysisMetricsService';
import { useTranslation } from "../../../locales";
import { useLanguageStore } from "../../../stores/languageStore";

// HRV 분석 지수 타입 정의
interface PPGIndexes {
  heartRate: number;
  rmssd: number;
  sdnn: number;
  pnn50: number;
  lfPower: number;
  hfPower: number;
  lfHfRatio: number;
  stressIndex: number;
  spo2?: number;
  avnn: number;
  pnn20: number;
  sdsd: number;
  hrMax: number;
  hrMin: number;
}

const PPGIndexesChart: React.FC = () => {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const { connectionState } = useDeviceStore();
  const ppgAnalysis = usePPGAnalysis();
  const ppgSQIData = usePPGSQIData();
  const isConnected = useConnectionState();
  const { isSensorContacted, leadOffStatus } = useSensorContactStatus();
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  // Store 기반 Moving Average 데이터 사용
  const ppgMovingAverage = usePPGMovingAverage();

  // processedDataStore에서 PPG 인덱스 데이터 사용
  const ppgIndices = ppgAnalysis.indices;

  // 신호 품질 상태 계산 (80% 기준)
  const signalQuality = useMemo(() => {
    if (!ppgSQIData.overallSQI || ppgSQIData.overallSQI.length === 0) {
      return { average: 0, isGood: false };
    }
    
    // 최근 10개 샘플의 평균 SQI 계산
    const recentSQI = ppgSQIData.overallSQI.slice(-10);
    const average = recentSQI.reduce((sum, point) => sum + point.value, 0) / recentSQI.length;
    const isGood = average >= 80; // 80% 이상 조건
    
    return { average, isGood };
  }, [ppgSQIData.overallSQI]);

  // 데이터 품질 상태 (센서 접촉 + 신호 품질)
  const dataQuality = useMemo(() => {
    const sensorOk = isSensorContacted;
    const sqiOk = signalQuality.isGood;
    const bothOk = sensorOk && sqiOk;
    

    
    return {
      sensorContact: sensorOk,
      signalQuality: sqiOk,
      overall: bothOk,
      sqiValue: signalQuality.average
    };
  }, [isSensorContacted, signalQuality, ppgAnalysis]);

  // Store 기반 PPG Moving Average 업데이트
  useEffect(() => {
    if (!ppgIndices || !isConnected || !dataQuality.overall) {

      return;
    }

    // SQI 품질이 80% 이상인지 확인
    const sqiQuality = dataQuality.sqiValue >= 80;
    
    // Store의 updatePPGMovingAverage 액션 호출
    const updatePPGMovingAverage = useProcessedDataStore.getState().updatePPGMovingAverage;
    updatePPGMovingAverage(ppgIndices, sqiQuality);
    

  }, [ppgIndices, isConnected, dataQuality.overall, dataQuality.sqiValue]);

  // 🔧 AnalysisMetricsService의 3000개 버퍼 기반 HRV 지표들 사용
  const analysisService = AnalysisMetricsService.getInstance();
  
  // Store 기반 PPG 인덱스 데이터 사용
  const indexData = useMemo((): PPGIndexes => {

    
    if (!ppgIndices || !isConnected) {

      return {
        heartRate: 0,
        rmssd: 0,
        sdnn: 0,
        pnn50: 0,
        lfPower: 0,
        hfPower: 0,
        lfHfRatio: 0,
        stressIndex: 0,
        avnn: 0,
        pnn20: 0,
        sdsd: 0,
        hrMax: 0,
        hrMin: 0
      };
    }
    
    // 🔧 AnalysisMetricsService에서 3000개 버퍼 기반 HRV 지표들 가져오기
    const bufferStatus = analysisService.getRRBufferStatus();
    const serviceRMSSD = analysisService.getCurrentRMSSD();
    const serviceSDNN = analysisService.getCurrentSDNN();
    const serviceSDSD = analysisService.getCurrentSDSD();
    const serviceAVNN = analysisService.getCurrentAVNN();
    const servicePNN50 = analysisService.getCurrentPNN50();
    const servicePNN20 = analysisService.getCurrentPNN20();
    const serviceStressIndex = analysisService.getCurrentStressIndex();
    const serviceHRMax = analysisService.getCurrentHRMax();
    const serviceHRMin = analysisService.getCurrentHRMin();
    const serviceLfPower = analysisService.getCurrentLfPower();
    const serviceHfPower = analysisService.getCurrentHfPower();
    const serviceLfHfRatio = analysisService.getCurrentLfHfRatio();
    
    // 🔧 120개 버퍼가 준비되었거나 일부 값이라도 있으면 AnalysisMetricsService 값 우선 사용
    const useAnalysisServiceData = bufferStatus.isReady || (
      serviceRMSSD > 0 || serviceSDNN > 0 || serviceLfPower > 0 || serviceHfPower > 0 || 
      serviceStressIndex > 0 || serviceAVNN > 0 || servicePNN50 > 0 || servicePNN20 > 0
    );
    

    
    const finalData: PPGIndexes = {
      // 🔧 BPM도 moving average 우선 사용 (SpO2와 동일한 방식)
      heartRate: ppgMovingAverage.stabilizedValues.heartRate !== null && ppgMovingAverage.stabilizedValues.heartRate > 0 
        ? ppgMovingAverage.stabilizedValues.heartRate 
        : (ppgIndices.heartRate || 0),
      rmssd: ppgMovingAverage.stabilizedValues.rmssd !== null && ppgMovingAverage.stabilizedValues.rmssd > 0 
        ? ppgMovingAverage.stabilizedValues.rmssd 
        : (useAnalysisServiceData ? serviceRMSSD : (ppgIndices.rmssd || 0)),
      sdnn: ppgMovingAverage.stabilizedValues.sdnn !== null && ppgMovingAverage.stabilizedValues.sdnn > 0 
        ? ppgMovingAverage.stabilizedValues.sdnn 
        : (useAnalysisServiceData ? serviceSDNN : (ppgIndices.sdnn || 0)),
      pnn50: ppgMovingAverage.stabilizedValues.pnn50 !== null && ppgMovingAverage.stabilizedValues.pnn50 > 0 
        ? ppgMovingAverage.stabilizedValues.pnn50 
        : (useAnalysisServiceData ? servicePNN50 : (ppgIndices.pnn50 || 0)),
      // 🔧 LF/HF 값들도 moving average 우선 사용
      lfPower: ppgMovingAverage.stabilizedValues.lfPower !== null && ppgMovingAverage.stabilizedValues.lfPower > 0 
        ? ppgMovingAverage.stabilizedValues.lfPower 
        : (useAnalysisServiceData ? serviceLfPower : (ppgIndices.lfPower || 0)),
      hfPower: ppgMovingAverage.stabilizedValues.hfPower !== null && ppgMovingAverage.stabilizedValues.hfPower > 0 
        ? ppgMovingAverage.stabilizedValues.hfPower 
        : (useAnalysisServiceData ? serviceHfPower : (ppgIndices.hfPower || 0)),
      lfHfRatio: ppgMovingAverage.stabilizedValues.lfHfRatio !== null && ppgMovingAverage.stabilizedValues.lfHfRatio > 0 
        ? ppgMovingAverage.stabilizedValues.lfHfRatio 
        : (useAnalysisServiceData ? serviceLfHfRatio : (ppgIndices.lfHfRatio || 0)),
      stressIndex: ppgMovingAverage.stabilizedValues.stressIndex !== null && ppgMovingAverage.stabilizedValues.stressIndex > 0 
        ? ppgMovingAverage.stabilizedValues.stressIndex 
        : (useAnalysisServiceData ? serviceStressIndex : (ppgIndices.stressIndex || 0)),
      avnn: ppgMovingAverage.stabilizedValues.avnn !== null && ppgMovingAverage.stabilizedValues.avnn > 0 
        ? ppgMovingAverage.stabilizedValues.avnn 
        : (useAnalysisServiceData ? serviceAVNN : ((ppgIndices as any).avnn || 0)),
      pnn20: ppgMovingAverage.stabilizedValues.pnn20 !== null && ppgMovingAverage.stabilizedValues.pnn20 > 0 
        ? ppgMovingAverage.stabilizedValues.pnn20 
        : (useAnalysisServiceData ? servicePNN20 : ((ppgIndices as any).pnn20 || 0)),
      sdsd: ppgMovingAverage.stabilizedValues.sdsd !== null && ppgMovingAverage.stabilizedValues.sdsd > 0 
        ? ppgMovingAverage.stabilizedValues.sdsd 
        : (useAnalysisServiceData ? serviceSDSD : ((ppgIndices as any).sdsd || 0)),
      hrMax: ppgMovingAverage.stabilizedValues.hrMax !== null && ppgMovingAverage.stabilizedValues.hrMax > 0 
        ? ppgMovingAverage.stabilizedValues.hrMax 
        : (useAnalysisServiceData ? serviceHRMax : ((ppgIndices as any).hrMax || 0)),
      hrMin: ppgMovingAverage.stabilizedValues.hrMin !== null && ppgMovingAverage.stabilizedValues.hrMin > 0 
        ? ppgMovingAverage.stabilizedValues.hrMin 
        : (useAnalysisServiceData ? serviceHRMin : ((ppgIndices as any).hrMin || 0)),
      spo2: ppgMovingAverage.stabilizedValues.spo2 !== null ? ppgMovingAverage.stabilizedValues.spo2 : ((ppgIndices as any).spo2 || 0)
    };
    
    // 🔧 BPM, LF/HF 및 SpO2 값 디버깅 로그

    
    return finalData;
  }, [ppgIndices, isConnected, dataQuality.overall, dataQuality.sqiValue, analysisService]);

  // 블루투스 데이터 청크가 들어올 때마다 즉시 업데이트
  useEffect(() => {
    setCurrentTime(Date.now());
  }, [ppgAnalysis]);

  // 각 지표별 정상 범위 정의
  const getIndexStatus = (label: string, value: number) => {
    switch (label) {
      case 'BPM':
        if (value < 60) return { status: 'low', color: 'text-indigo-400', message: t.visualizer.ppgBpmLowMsg };
        if (value > 100) return { status: 'high', color: 'text-slate-300', message: t.visualizer.ppgBpmHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgBpmNormalMsg };
      
      case 'SpO2':
        if (value === 0) return { status: 'low', color: 'text-muted-foreground', message: t.visualizer.ppgSpo2UnmeasurableMsg };
        if (value < 90) return { status: 'high', color: 'text-slate-300', message: t.visualizer.ppgSpo2SevereMsg };
        if (value < 95) return { status: 'high', color: 'text-slate-400', message: t.visualizer.ppgSpo2MildMsg };
        if (value < 98) return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgSpo2LowNormalMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgSpo2NormalMsg };
      
      case 'RMSSD':
        if (value < 20) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.ppgRmssdLowMsg };
        if (value > 50) return { status: 'high', color: 'text-indigo-400', message: t.visualizer.ppgRmssdHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgRmssdNormalMsg };
      
      case 'SDNN':
        if (value < 30) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.ppgSdnnLowMsg };
        if (value > 100) return { status: 'high', color: 'text-indigo-400', message: t.visualizer.ppgSdnnHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgSdnnNormalMsg };
      
      case 'PNN50':
        if (value < 10) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.ppgPnn50LowMsg };
        if (value > 30) return { status: 'high', color: 'text-indigo-400', message: t.visualizer.ppgPnn50HighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgPnn50NormalMsg };
      
      case 'Stress':
        if (value < 0.30) return { status: 'low', color: 'text-indigo-400', message: t.visualizer.ppgStressVeryLowMsg };
        if (value < 0.50) return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgStressLowMsg };
        if (value < 0.70) return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgStressNormalMsg };
        if (value < 0.90) return { status: 'high', color: 'text-slate-400', message: t.visualizer.ppgStressHighMsg };
        return { status: 'high', color: 'text-slate-300', message: t.visualizer.ppgStressVeryHighMsg };
      
      // 새로운 지표들 추가
      case 'AVNN':
        if (value < 600) return { status: 'high', color: 'text-slate-300', message: t.visualizer.ppgAvnnHighMsg };
        if (value > 1000) return { status: 'low', color: 'text-indigo-400', message: t.visualizer.ppgAvnnLowMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgAvnnNormalMsg };
      
      case 'PNN20':
        if (value < 20) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.ppgPnn20LowMsg };
        if (value > 60) return { status: 'high', color: 'text-indigo-400', message: t.visualizer.ppgPnn50HighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgPnn20NormalMsg };
      
      case 'SDSD':
        if (value < 15) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.ppgSdsdLowMsg };
        if (value > 40) return { status: 'high', color: 'text-indigo-400', message: t.visualizer.ppgSdsdHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgSdsdNormalMsg };
      
      case 'HR Max':
        if (value < 80) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.ppgHrMaxLowMsg };
        if (value > 150) return { status: 'high', color: 'text-slate-300', message: t.visualizer.ppgHrMaxHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgHrMaxNormalMsg };
      
      case 'HR Min':
        if (value < 50) return { status: 'low', color: 'text-indigo-400', message: t.visualizer.ppgHrMinLowMsg };
        if (value > 80) return { status: 'high', color: 'text-slate-400', message: t.visualizer.ppgHrMinHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgHrMinNormalMsg };
      
      // 주파수 도메인 분석 지표들 (100배 스케일링 기반 정상 범위)
      case 'LF':
        if (value < 2) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.ppgLfLowMsg };
        if (value > 12) return { status: 'high', color: 'text-slate-300', message: t.visualizer.ppgLfHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgLfNormalMsg };
      
      case 'HF':
        if (value < 0.8) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.ppgHfLowMsg };
        if (value > 40) return { status: 'high', color: 'text-indigo-400', message: t.visualizer.ppgHfHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgHfNormalMsg };
      
      case 'LF/HF':
        if (value < 1.0) return { status: 'low', color: 'text-indigo-400', message: t.visualizer.ppgLfhfLowMsg };
        if (value > 10.0) return { status: 'high', color: 'text-slate-300', message: t.visualizer.ppgLfhfHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgLfhfNormalMsg };
      
      default:
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.ppgBpmNormalMsg };
    }
  };

  // 안정화 상태 계산 (EEG 방식과 동일)
  const stabilizationStatus = useMemo(() => {
    const hasEnoughSamples = ppgMovingAverage.history.heartRate.length >= 10;
    const isQualityGood = dataQuality.overall;
    const isStabilized = hasEnoughSamples && isQualityGood;
    
    return {
      isStabilized,
      hasEnoughSamples,
      isQualityGood,
      sampleCount: ppgMovingAverage.history.heartRate.length,
      requiredSamples: 10
    };
  }, [ppgMovingAverage.history.heartRate.length, dataQuality.overall]);

  // 카드 데이터 정의 - 새로운 배치 순서 적용
  const cardData = [
    // 1행: BPM, SpO2, HR Max, HR Min (4개)
    {
      label: 'BPM',
      value: indexData.heartRate,
      unit: 'BPM',
      color: 'bg-red-500',
      description: t.visualizer.ppgBpmDesc,
      historyLength: ppgMovingAverage.history.heartRate.length
    },
    {
      label: 'SpO2',
      value: indexData.spo2,
      unit: '%',
      color: 'bg-cyan-500',
      description: t.visualizer.ppgSpo2Desc,
      historyLength: ppgMovingAverage.history.spo2.length
    },
    {
      label: 'HR Max',
      value: (indexData as any).hrMax || 0,
      unit: 'BPM',
      color: 'bg-rose-500',
      description: t.visualizer.ppgHrMaxDesc,
      historyLength: ppgMovingAverage.history.hrMax.length
    },
    {
      label: 'HR Min',
      value: (indexData as any).hrMin || 0,
      unit: 'BPM',
      color: 'bg-emerald-500',
      description: t.visualizer.ppgHrMinDesc,
      historyLength: ppgMovingAverage.history.hrMin.length
    },
    // 2행: Stress, RMSSD, SDNN, SDSD (4개)
    {
      label: 'Stress',
      value: indexData.stressIndex,
      unit: '',
      color: 'bg-purple-500',
      description: t.visualizer.ppgStressIdxDesc,
      historyLength: ppgMovingAverage.history.stressIndex.length
    },
    {
      label: 'RMSSD',
      value: indexData.rmssd,
      unit: 'ms',
      color: 'bg-indigo-500',
      description: t.visualizer.ppgRmssdDesc,
      historyLength: ppgMovingAverage.history.rmssd.length
    },
    {
      label: 'SDNN',
      value: indexData.sdnn,
      unit: 'ms',
      color: 'bg-green-500/10',
      description: t.visualizer.ppgSdnnDesc,
      historyLength: ppgMovingAverage.history.sdnn.length
    },
    {
      label: 'SDSD',
      value: (indexData as any).sdsd || 0,
      unit: 'ms',
      color: 'bg-teal-500',
      description: t.visualizer.ppgSdsdDesc,
      historyLength: ppgMovingAverage.history.sdsd.length
    },
    // 3행: LF, HF, LF/HF (3개) - 주파수 도메인 분석
    {
      label: 'LF',
      value: indexData.lfPower || 0,
      unit: 'ms²',
      color: 'bg-violet-500',
      description: t.visualizer.ppgLfDesc,
      historyLength: ppgMovingAverage.history.lfPower.length
    },
    {
      label: 'HF',
      value: indexData.hfPower || 0,
      unit: 'ms²',
      color: 'bg-pink-500',
      description: t.visualizer.ppgHfDesc,
      historyLength: ppgMovingAverage.history.hfPower.length
    },
    {
      label: 'LF/HF',
      value: indexData.lfHfRatio || 0,
      unit: '',
      color: 'bg-amber-500',
      description: t.visualizer.ppgLfhfDesc,
      historyLength: ppgMovingAverage.history.lfHfRatio.length
    },
    // 4행: AVNN, PNN50, PNN20 (3개)
    {
      label: 'AVNN',
      value: (indexData as any).avnn || 0,
      unit: 'ms',
      color: 'bg-indigo-500',
      description: t.visualizer.ppgAvnnDesc,
      historyLength: ppgMovingAverage.history.avnn.length
    },
    {
      label: 'PNN50',
      value: indexData.pnn50,
      unit: '%',
      color: 'bg-yellow-500/10',
      description: t.visualizer.ppgPnn50IdxDesc,
      historyLength: ppgMovingAverage.history.pnn50.length
    },
    {
      label: 'PNN20',
      value: (indexData as any).pnn20 || 0,
      unit: '%',
      color: 'bg-orange-500',
      description: t.visualizer.ppgPnn20IdxDesc,
      historyLength: ppgMovingAverage.history.pnn20.length
    }
  ];

  // 카드 렌더링 함수 (EEG 방식과 동일)
  const renderCard = (card: any) => {
    const isStabilized = dataQuality.overall && card.historyLength >= 10;
    const cardOpacity = dataQuality.overall ? 'opacity-100' : 'opacity-60';
    
    // 상태별 색상과 설명 가져오기
    const isValidValue = card.value !== null && card.value !== undefined && card.value > 0;
    
    const indexStatus = isValidValue
      ? getIndexStatus(card.label, card.value) 
      : { status: 'normal', color: 'text-muted-foreground', message: t.visualizer.indexNoData };
    
    // 상태별 마커 색상 결정 (지표별 개별 매핑)
    const getMarkerColor = () => {
      if (!isValidValue) {
        return 'bg-muted'; // 데이터 없음
      }
      
      // 지표별 색상 매핑 (상태 메시지와 일치하도록)
      switch (card.label) {
        case 'BPM':
          if (indexStatus.status === 'low') return 'bg-indigo-500';    // 낮은 심박수
          if (indexStatus.status === 'high') return 'bg-slate-400';   // 높은 심박수
          return 'bg-green-500/10'; // 정상
          
        case 'SpO2':
          if (indexStatus.status === 'low') return 'bg-muted';   // 측정 불가
          if (indexStatus.status === 'high') return 'bg-slate-400';  // 산소포화도 낮음
          return 'bg-green-500/10'; // 정상
          
        case 'Stress':
          if (indexStatus.status === 'low') return 'bg-indigo-500';   // 과도한 이완
          if (indexStatus.status === 'high') return 'bg-slate-400';  // 높은 스트레스
          return 'bg-green-500/10'; // 정상
          
        case 'RMSSD':
        case 'SDNN':
        case 'PNN50':
        case 'PNN20':
        case 'SDSD':
          if (indexStatus.status === 'low') return 'bg-yellow-500/10';  // 긴장/피로 상태
          if (indexStatus.status === 'high') return 'bg-indigo-500';   // 심박 변동 큼
          return 'bg-green-500/10'; // 정상
          
        case 'AVNN':
          if (indexStatus.status === 'low') return 'bg-indigo-500';   // 느린 심박 (좋음)
          if (indexStatus.status === 'high') return 'bg-slate-400';  // 빠른 심박
          return 'bg-green-500/10'; // 정상
          
        case 'HR Max':
          if (indexStatus.status === 'low') return 'bg-yellow-500/10'; // 낮은 최대 심박수
          if (indexStatus.status === 'high') return 'bg-slate-400';  // 높은 최대 심박수
          return 'bg-green-500/10'; // 정상
          
        case 'HR Min':
          if (indexStatus.status === 'low') return 'bg-indigo-500';   // 낮은 최소 심박수 (좋음)
          if (indexStatus.status === 'high') return 'bg-slate-500'; // 높은 최소 심박수
          return 'bg-green-500/10'; // 정상
          
        case 'LF':
          if (indexStatus.status === 'low') return 'bg-yellow-500/10'; // 과도한 휴식
          if (indexStatus.status === 'high') return 'bg-slate-400';  // 스트레스/긴장
          return 'bg-green-500/10'; // 정상
          
        case 'HF':
          if (indexStatus.status === 'low') return 'bg-yellow-500/10'; // 스트레스/피로
          if (indexStatus.status === 'high') return 'bg-indigo-500';  // 깊은 휴식 (좋음)
          return 'bg-green-500/10'; // 정상
          
        case 'LF/HF':
          if (indexStatus.status === 'low') return 'bg-indigo-500';   // 부교감신경 우세 (좋음)
          if (indexStatus.status === 'high') return 'bg-slate-400';  // 교감신경 우세 (스트레스)
          return 'bg-green-500/10'; // 균형 상태
          
        default:
          // 기본 로직 (이전과 동일)
          switch (indexStatus.status) {
            case 'low': return 'bg-yellow-500/10';
            case 'high': return 'bg-slate-400';
            case 'normal': return 'bg-green-500/10';
            default: return 'bg-muted';
          }
      }
    };

    return (
      <TooltipProvider key={card.label}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 hover:bg-white/[0.05] transition-colors ${cardOpacity}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getMarkerColor()}`} />
                  <span className="text-sm font-medium text-foreground">{card.label}</span>
                  {/* 안정화 상태 표시 */}
                  {isStabilized && (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-indigo-900/30 text-indigo-400 border border-indigo-500/30 rounded text-xs">
                      <CheckCircle className="w-2.5 h-2.5" strokeWidth={1.5} />
                      <span className="text-xs">{t.visualizer.stabilizing}</span>
                    </div>
                  )}
                </div>
                {/* 품질 상태 표시 */}
                {!dataQuality.overall && (
                  <Clock className="w-3 h-3 text-muted-foreground" strokeWidth={1.5} />
                )}
              </div>
              <div className="text-2xl font-bold text-white mb-1">
                {isValidValue ? card.value.toFixed(
                  card.label === 'BPM' ? 0 : 
                  card.label === 'Stress' ? 2 : 
                  card.label === 'LF' || card.label === 'HF' ? 1 : // LF/HF는 소수점 1자리 (ms² 단위)
                  card.label === 'LF/HF' ? 2 : 1  // LF/HF 비율은 소수점 2자리
                ) : '--'}
                <span className="text-sm text-muted-foreground ml-1">{card.unit}</span>
              </div>
              {/* 상태 메시지 */}
              <div className={`text-xs ${indexStatus.color} mb-1`}>
                {indexStatus.message}
              </div>
              {/* 히스토리 정보 */}
              {isStabilized && (
                <div className="text-xs text-muted-foreground">
                  {card.historyLength}{t.visualizer.indexSampleAvg}
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-slate-900 border border-slate-700 shadow-xl">
            <div className="max-w-lg p-4 bg-card rounded-lg">
              <div 
                className="text-sm leading-relaxed space-y-2 [&>strong]:text-indigo-400 [&>strong]:font-semibold [&>br]:block [&>br]:mb-1"
                style={{
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}
                dangerouslySetInnerHTML={{ 
                  __html: (getIndexGuide(card.label, currentLanguage === 'ko' ? 'ko' : 'en') || `<strong>${card.label}</strong><br/>${t.visualizer.indexDetailPreparing}`)
                    .replace(/<strong>/g, '<strong class="text-indigo-400 font-semibold">')
                    .replace(new RegExp(t.visualizer.tooltipFormula + ':', 'g'), '<span class="text-green-300 font-medium">' + t.visualizer.tooltipFormula + ':</span>')
                    .replace(new RegExp(t.visualizer.tooltipNormalRange + ':', 'g'), '<span class="text-yellow-300 font-medium">' + t.visualizer.tooltipNormalRange + ':</span>')
                    .replace(new RegExp(t.visualizer.tooltipInterpretation + ':', 'g'), '<span class="text-orange-300 font-medium">' + t.visualizer.tooltipInterpretation + ':</span>')
                    .replace(new RegExp(t.visualizer.tooltipReference + ':', 'g'), '<span class="text-purple-300 font-medium">' + t.visualizer.tooltipReference + ':</span>')
                    .replace(new RegExp(t.visualizer.tooltipDescription + ':', 'g'), '<span class="text-cyan-300 font-medium">' + t.visualizer.tooltipDescription + ':</span>')
                }}
              />
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // 데이터 품질에 따른 배경색 결정
  const getQualityBackgroundColor = () => {
    if (!indexData || !isConnected) {
      return 'bg-muted dark:bg-background';
    }
    
    // SQI 기반 품질 평가
    if (dataQuality.sqiValue >= 80) {
      return 'bg-green-500/10 bg-green-500/10';
    } else if (dataQuality.sqiValue >= 60) {
      return 'bg-yellow-500/10 bg-yellow-500/10';
    } else {
      return 'bg-red-50 bg-red-500/10';
    }
  };

  // 연결되지 않았거나 데이터가 없는 경우 메시지 표시
  if (!isConnected || !indexData) {
    return (
      <div className="w-full space-y-6">
        <div className="w-full h-80 flex items-center justify-center">
          <div className="text-center space-y-4">
            
            <div className="text-lg text-foreground">
              {t.visualizer.noGraphData}
            </div>
            <div className="text-sm text-muted-foreground">
              {t.visualizer.connectDevice}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {t.visualizer.ppgConnectToView}
            </div>
            <div className="text-xs text-muted-foreground mt-3 p-2 bg-card rounded">
              {t.visualizer.ppgAnalyzing}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">

      {/* 센서 접촉 상태 경고 */}
      {isConnected && !isSensorContacted && (
        <div className="mb-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-400" strokeWidth={1.5} />
          <div className="text-red-300">
            <div className="font-medium">{t.visualizer.poorContactDetection}</div>
            <div className="text-sm">
              FP1: {leadOffStatus.fp1 ? t.visualizer.contactPoor : t.visualizer.contactNormal}, 
              FP2: {leadOffStatus.fp2 ? t.visualizer.contactPoor : t.visualizer.contactNormal} - 
              {t.visualizer.signalQualityMayDegrade}
            </div>
          </div>
        </div>
      )}

      {/* 타이틀과 연결 상태 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="lb-h3">{t.visualizer.ppgHrvTitle}</h3>
          {/* 안정화 상태 표시 (EEG 방식과 동일) */}
          {stabilizationStatus.isStabilized ? (
            <div className="flex items-center gap-1 px-2 py-1 bg-green-900/30 text-green-300 border border-green-500/30 rounded-full text-xs">
              <CheckCircle className="w-3 h-3" strokeWidth={1.5} />
              <span>안정화됨 ({stabilizationStatus.sampleCount}개 샘플)</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 px-2 py-1 bg-yellow-900/30 text-yellow-300 border border-yellow-500/30 rounded-full text-xs">
              <Clock className="w-3 h-3" strokeWidth={1.5} />
              <span>
                {!stabilizationStatus.isQualityGood 
                  ? t.visualizer.ppgSignalQualityNeed 
                  : `${t.visualizer.ppgStabilizing} (${stabilizationStatus.sampleCount}/${stabilizationStatus.requiredSamples})`
                }
              </span>
            </div>
          )}
        </div>
        {(!isConnected || connectionState.status !== 'connected') && (
          <span className="text-sm text-muted-foreground">
            {t.visualizer.indexDeviceRequired}
          </span>
        )}
      </div>

      {/* PPG 지수 카드들 - 새로운 배치 (4-4-3-3) */}
      <div className="space-y-4">
        {/* 1행: BPM, SpO2, HR Max, HR Min (4개) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cardData.slice(0, 4).map((card) => renderCard(card))}
        </div>
        
        {/* 2행: Stress, RMSSD, SDNN, SDSD (4개) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cardData.slice(4, 8).map((card) => renderCard(card))}
        </div>

        {/* 3행: LF, HF, LF/HF (3개, 꽉 차게) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {cardData.slice(8, 11).map((card) => renderCard(card))}
        </div>

        {/* 4행: AVNN, PNN50, PNN20 (3개, 꽉 차게) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {cardData.slice(11, 14).map((card) => renderCard(card))}
        </div>
      </div>

      {/* 비의료기기 면책 문구 — 지표가 임상 진단으로 오인되지 않도록 상시 표시 */}
      <p className="text-xs text-muted-foreground">{t.visualizer.indexDisclaimer}</p>
    </div>
  );
};

export default PPGIndexesChart; 